<template>
  <ScenarioTabWrapper
    title="Sanierungstiefe"
    :enable-collapse="true"
    :read-only
    :apply-selection-enabled="
      props.scenario.municipalities.asList.length > 0 &&
      refurbishmentCopy.isValid(refurbishmentCopy.sectors)
    "
    @all-selection-button-click="
      emit('setRefurbishmentValues', {
        sectors: refurbishmentCopy.sectors,
        unit: refurbishmentCopy.unit,
      })
    "
  >
    <TextBox v-if="!readOnly"
      >Standardwerte aus dem KWW-Technikkatalog sind bereits hinterlegt.
      Überprüfe und bearbeite diese gegebenfalls.</TextBox
    >
    <div class="flex w-full justify-between">
      <div
        v-for="sector in refurbishmentCopy.sectors"
        :key="sector.key"
        class="flex flex-col gap-5"
      >
        <p class="body-2 text-title-color2">{{ sector.label }}</p>
        <div v-for="field in sector.fields" :key="field.key">
          <InputEl
            v-model="field.value"
            :label="field.label"
            input-type="integer"
            :disabled="readOnly"
            :rules="{
              required: true,
              range: [
                refurbishmentCopy.validationRules.min,
                refurbishmentCopy.validationRules.max,
                'float',
              ],
            }"
            :suffix="refurbishmentCopy.unit === 0 ? 'kWh/m²' : '%'"
          />
        </div>
      </div>
    </div>
    <DropDown
      v-model="refurbishmentCopy.unit"
      :items-data="refurbishmentCopy.unitOptions"
      items-data-key="display_name"
      :disabled="readOnly"
      class="mt-5 w-full"
      label="Einheit Sanierungstiefe"
    />
  </ScenarioTabWrapper>
  <ScenarioTabWrapper
    title="Sanierungsquote"
    :enable-collapse="true"
    :read-only
    :available-items="availableItems"
    :filtered-items="districtsforEfficiency"
    :selected-items="selectedItems"
    :headers="tableHeaders.efficiency"
    :key-name-for-checkbox="'district'"
    :apply-selection-enabled="
      props.scenario.municipalities.asList.length > 0 && refurbishmentRateValid
    "
    :granular-selection-enabled="
      districtParameterizations.some((item) => {
        return item.refurbishment_rate !== null;
      })
    "
    @all-selection-button-click="
      emitData(districtParameterizations.map((d) => d.district))
    "
    @granular-selection-button-click="emitData(selectedItems)"
    @update:filter="filters = $event"
    @item-click="selectedItems = [...$event]"
  >
    <InputEl
      v-if="!readOnly"
      v-model="refurbishmentRate"
      :rules="{ required: true, range: [0, 100, 'float'] }"
      class="w-[386px]"
      label="Sanierungsquote"
      input-type="float"
      suffix="% p.a."
      data-test="refurbishment-rate-input"
      tooltip-text="Anteil der Gebäude, die jährlich saniert werden sollen"
    />
  </ScenarioTabWrapper>
</template>

<script setup>
import { tableHeaders } from '@/apps/scenarios/scenario-create-data';
import { computed, reactive, ref, watch } from 'vue';
import ScenarioTabWrapper from '@/apps/scenarios/components/shared/ScenarioTabWrapper.vue';
import InputEl from '@/components/storybook/src/stories/input/InputEl.vue';
import DropDown from '@/components/storybook/src/stories/DropDown/DropDown.vue';
import { isNullOrUndefined } from '@/utils/generalUtils';
import { useDetailFiltering } from '@/apps/scenarios/detailFiltering';
import TextBox from '@/components/storybook/src/stories/textBox/TextBox.vue';
import Refurbishment from '@/apps/scenarios/classes/refurbishment';

const props = defineProps({
  scenario: Object,
  activeTab: Number,
  districtParameterizations: Array,
  refurbishment: Object,
  availableItems: Object,
  readOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['setRefurbishmentValues', 'setRefurbishmentRate']);

const { selectedItems, filters, filteredItems } = useDetailFiltering(
  props.districtParameterizations,
  props.availableItems,
  ['refurbishment_rate'],
);

const refurbishmentCopy = reactive(deepCopyRefurbishment(props.refurbishment));

const districtsforEfficiency = computed(() => {
  return filteredItems.value.map((district) => {
    const { refurbishment_value, refurbishment_rate, refurbishment_unit } =
      district;
    const refurbishmentValue = isNullOrUndefined(refurbishment_value)
      ? '-'
      : `${refurbishment_value} ${refurbishment_unit === 0 ? 'kWh/m²' : '%'}`;
    const refurbishmentRate = isNullOrUndefined(refurbishment_rate)
      ? '-'
      : `${refurbishment_rate} % p.a.`;

    return {
      ...district,
      refurbishmentValue,
      refurbishmentRate,
    };
  });
});

const refurbishmentRate = ref(null);
const refurbishmentRateValid = computed(
  () =>
    refurbishmentRate.value !== null &&
    refurbishmentRate.value <= 100 &&
    refurbishmentRate.value >= 0,
);

function emitData(districtIds) {
  const updateDistrictParameter = {};
  for (const districtId of districtIds) {
    updateDistrictParameter[districtId] = {
      refurbishment_rate: refurbishmentRate.value,
    };
  }
  emit('setRefurbishmentRate', updateDistrictParameter);
}

function deepCopyRefurbishment(original) {
  const optionsClone = JSON.parse(JSON.stringify(original.options));
  const copy = new Refurbishment(optionsClone);

  copy.unit = original.unit;

  // If the object has been modified after initialization
  if (original.sectors && original.sectors.length > 0) {
    original.sectors.forEach((sector, sectorIndex) => {
      sector.fields.forEach((field, fieldIndex) => {
        // Copy field values to maintain any user edits
        copy.sectors[sectorIndex].fields[fieldIndex].value = field.value;
      });
    });
  }

  return copy;
}

// The default values are different depending on the unit
watch(
  () => refurbishmentCopy.unit,
  () => {
    refurbishmentCopy.sectors = refurbishmentCopy.getSectors();
  },
);
</script>
